import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { RecurringSeriesOccurrence } from '@spike/new-booking-model';
import { RecurringSeriesViewStatus } from '@spike/new-bookings-action';
import Button from 'components/UI/V3/Button/Button';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';
import { AlertMessage } from 'components/UI/V3/AlertMessage/AlertMessage';
import { FunctionComponent } from 'react';
import BookingsSeriesSection from './BookingsSeriesSection';
import SeriesSummaryCards from './SeriesSummaryCards';
import { RecurringSeriesView } from './useRecurringSeriesView';
import styles from './RecurringSeriesView.module.scss';

interface RecurringSeriesContentProps {
    series: RecurringSeriesView;
    isUpdating?: boolean;
    selectedBookingId?: number;
    onSelectBooking?: (occurrence: RecurringSeriesOccurrence) => void;
}

export const RecurringSeriesContent: FunctionComponent<RecurringSeriesContentProps> = props => {
    const { series } = props;

    if (series.error === 'not_found') {
        return (
            <div className={styles.state}>
                <p className={styles.stateText}>This recurring series could not be found.</p>
            </div>
        );
    }

    if (series.error === 'unknown') {
        return (
            <div className={styles.state}>
                <p className={styles.stateText}>We couldn&apos;t load this series.</p>
                <Button
                    variant="outlined"
                    size="medium"
                    label="Retry"
                    className={styles.retryButton}
                    onClick={series.retry}
                />
            </div>
        );
    }

    if (!series.detail) {
        return (
            <div className={styles.state}>
                <Spinner />
            </div>
        );
    }

    return (
        <>
            <SeriesSummaryCards detail={series.detail} />

            <AlertMessage
                icon={faCircleInfo}
                bgColor="#f8f5f1"
                bdColor="#bcb8ae"
                iconColor="#baa997"
                bWidth="2px"
                className={styles.banner}
            >
                <p className={styles.bannerText}>
                    Changes to the template or recurrence rule will update future orders in this series.
                </p>
            </AlertMessage>

            <BookingsSeriesSection
                detail={series.detail}
                isPageLoading={series.status === RecurringSeriesViewStatus.Fetching || Boolean(props.isUpdating)}
                filter={series.filter}
                onRequestPage={series.requestPage}
                onChangeFilter={series.changeFilter}
                selectedBookingId={props.selectedBookingId}
                onSelectBooking={props.onSelectBooking}
            />
        </>
    );
};

interface RecurringSeriesTotalsProps {
    series: RecurringSeriesView;
}

export const RecurringSeriesTotals: FunctionComponent<RecurringSeriesTotalsProps> = ({ series }) => {
    if (!series.detail || series.error) {
        return null;
    }

    return (
        <p className={styles.totals}>
            {series.detail.ordersCount ?? series.detail.progress.created} Orders
            {series.detail.invoicesCount !== undefined && ` · ${series.detail.invoicesCount} Invoices`}
        </p>
    );
};

export default RecurringSeriesContent;
